<template>
  <div class="game-toolbar" v-if="game.state.screen === 'playing'">
    <ion-button @click="returnToHome" fill="clear">
      <ion-icon :icon="arrowBackOutline" slot="icon-only"></ion-icon>
    </ion-button>
    <ion-button @click="restartLevel" fill="clear">
      <ion-icon :icon="refreshOutline" slot="icon-only"></ion-icon>
    </ion-button>
    <ion-button v-if="canShowTutorialManually" @click="showTutorial" fill="clear">
      <ion-icon :icon="helpCircleOutline" slot="icon-only"></ion-icon>
    </ion-button>
    <ion-button v-if="canShowVision" @click="isVisionConfirmOpen = true" fill="clear">
      <ion-icon :icon="colorWandOutline" slot="icon-only"></ion-icon>
    </ion-button>
  </div>

  <ion-modal :is-open="isVisionConfirmOpen" @didDismiss="isVisionConfirmOpen = false" class="card-modal">
    <ion-card>
      <ion-card-header>
        <ion-card-title>Magic Vision</ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <p>Struggling? Use magic to show a vision of how to win this battle!</p>
        <p>You'll still need to do it yourself but this will show you how it's done.</p>
        <p>The magic vision will be recorded in your game progress.</p>
        <p>Continue?</p>
        <ion-button @click="showVision" expand="block">Show me</ion-button>
        <ion-button @click="isVisionConfirmOpen = false" expand="block" fill="outline">Cancel</ion-button>
      </ion-card-content>
    </ion-card>
  </ion-modal>
</template>

<script setup>
import { inject, computed, ref } from 'vue';
import { IonButton, IonIcon, IonModal, IonCard, IonCardHeader, IonCardTitle, IonCardContent } from '@ionic/vue';
import { arrowBackOutline, refreshOutline, helpCircleOutline, colorWandOutline } from 'ionicons/icons';
import { GameEvents } from '../lib/GameEvents';

const game = inject('game');
const isVisionConfirmOpen = ref(false);

const canShowTutorialManually = computed(() => {
  const levelKey = game.state.gameplay.currentLevelKey;
  if (!levelKey || !game.state.tutorial.availableTutorial) {
    return false;
  }
  // Always available for completed levels; otherwise only after tutorial has been auto-shown
  if (game.state.progress.completedLevelIds[levelKey]) return true;
  return game.state.progress.levelStates[levelKey]?.tutorialShown;
});

const canShowVision = computed(() => {
  const levelKey = game.state.gameplay.currentLevelKey;
  if (!levelKey) return false;
  if (game.state.activePlayMode === 'replay') return false;

  // Always available for completed levels
  if (game.state.progress.completedLevelIds[levelKey]) return true;

  const levelState = game.state.progress.levelStates[levelKey];
  if (!levelState) return false;

  // Use tutorial failure threshold if available, otherwise default to 1
  const tutorial = game.state.tutorial.availableTutorial;
  const threshold = tutorial?.failures ?? 1;
  return levelState.failures >= threshold;
});

const returnToHome = () => {
  if (game.state.activePlayMode === 'replay') {
    game.emit(GameEvents.EXIT_SOLUTION_REPLAY);
  } else {
    game.emit(GameEvents.RETURN_TO_HOME);
  }
};
const restartLevel = () => game.dispatch(GameEvents.RESTART_GAME);
const showTutorial = () => game.emit(GameEvents.SHOW_TUTORIAL);

const showVision = () => {
  const levelKey = game.state.gameplay.currentLevelKey;
  if (!levelKey) return;

  game.emit(GameEvents.SHOW_VISION, { levelKey });
  isVisionConfirmOpen.value = false;
};
</script>

<style scoped>
.game-toolbar {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 5px;
  background-color: var(--hw-bg-overlay);
  border: 1px solid var(--hw-border);
  padding: 5px;
  border-radius: var(--hw-radius);
  z-index: 1000;
}

.game-toolbar ion-button {
  font-size: 24px;
  color: var(--hw-text);
}

ion-card {
  --background: var(--hw-bg-surface);
  --color: var(--hw-text);
  text-align: center;
  padding: 20px;
}

ion-card-title {
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 20px;
}
</style>
